import React, { useCallback, useEffect, useRef, useState } from "react";
import type { Story } from "@/types";
import { mediaUrl } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";

const SEEN_KEY = "kms.storiesSeen";
const DURATION = 5000;

function loadSeen(): string[] {
  try {
    return JSON.parse(localStorage.getItem(SEEN_KEY) ?? "[]");
  } catch {
    return [];
  }
}

/** Telegram-style story circles; unseen ones keep the navy ring. */
export function StoriesRow({ stories }: { stories: Story[] }) {
  const [seen, setSeen] = useState<string[]>(loadSeen);
  const [openAt, setOpenAt] = useState<number | null>(null);
  if (stories.length === 0) return null;

  const markSeen = (id: string) =>
    setSeen((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id].slice(-100);
      try {
        localStorage.setItem(SEEN_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });

  return (
    <>
      <div className="stories">
        {stories.map((s, i) => (
          <button key={s.id} className="story" onClick={() => setOpenAt(i)}>
            <span className={`story__ring ${seen.includes(s.id) ? "story__ring--seen" : ""}`}>
              <span className="story__img">{s.coverImage ? <img src={mediaUrl(s.coverImage)} alt="" /> : <span className="story__empty" />}</span>
            </span>
            <span className="story__label">{s.storyLabel || s.title}</span>
          </button>
        ))}
      </div>
      {openAt !== null && <StoryViewer stories={stories} start={openAt} onSeen={markSeen} onClose={() => setOpenAt(null)} />}
    </>
  );
}

function StoryViewer({ stories, start, onSeen, onClose }: { stories: Story[]; start: number; onSeen: (id: string) => void; onClose: () => void }) {
  const { t } = useI18n();
  const { navigate } = useRouter();
  const [index, setIndex] = useState(start);
  const [paused, setPaused] = useState(false);
  const startedAt = useRef(Date.now());
  const elapsedBeforePause = useRef(0);
  const [progress, setProgress] = useState(0);
  const story = stories[index];

  const go = useCallback(
    (delta: number) => {
      const next = index + delta;
      if (next < 0) return;
      if (next >= stories.length) return onClose();
      setIndex(next);
    },
    [index, stories.length, onClose],
  );

  useEffect(() => {
    onSeen(story.id);
    startedAt.current = Date.now();
    elapsedBeforePause.current = 0;
    setProgress(0);
  }, [story.id]);

  useEffect(() => {
    if (paused) {
      elapsedBeforePause.current += Date.now() - startedAt.current;
      return;
    }
    startedAt.current = Date.now();
    const timer = setInterval(() => {
      const p = (elapsedBeforePause.current + Date.now() - startedAt.current) / DURATION;
      if (p >= 1) {
        clearInterval(timer);
        go(1);
      } else setProgress(p);
    }, 50);
    return () => clearInterval(timer);
  }, [paused, index, go]);

  return (
    <div className="story-viewer" role="dialog" aria-modal="true">
      <div className="story-viewer__bars">
        {stories.map((s, i) => (
          <span key={s.id}>
            <i style={{ width: `${i < index ? 100 : i === index ? progress * 100 : 0}%` }} />
          </span>
        ))}
      </div>
      <button className="story-viewer__close" onClick={onClose} aria-label={t("common.close")}>
        ✕
      </button>
      <div
        className="story-viewer__media"
        onPointerDown={() => setPaused(true)}
        onPointerUp={() => setPaused(false)}
        onPointerLeave={() => setPaused(false)}
      >
        {story.coverImage ? <img src={mediaUrl(story.coverImage)} alt="" /> : <div className="story-viewer__blank" />}
        <button className="story-viewer__tap story-viewer__tap--prev" onClick={() => go(-1)} aria-label="‹" />
        <button className="story-viewer__tap story-viewer__tap--next" onClick={() => go(1)} aria-label="›" />
      </div>
      <div className="story-viewer__caption">
        <h2>{story.title}</h2>
        {story.excerpt && <p>{story.excerpt}</p>}
        <button
          className="btn btn--primary"
          onClick={() => {
            onClose();
            navigate({ name: "post", slug: story.slug });
          }}
        >
          {t("stories.more")}
        </button>
      </div>
    </div>
  );
}
